import PropTypes from 'prop-types'
import { Button, Tooltip } from 'antd'
import {
  CalendarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import dayjs from 'dayjs'
import { formatDOB } from '../../lib/datetime'
import { reportShowPath, reportEditPath } from '../../lib/reports'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import DataExtractionIcon from '../Common/DataExtractionIcon'
import DiseasesTags from '../Common/DiseasesTags'
import PatientEntry from '../Common/PatientEntry'

// Widths of antd's selection and expand gutter columns. The expanded details are
// indented by their sum so the fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40
export const SELECTION_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  criticalIcon: {
    marginRight: 6,
    color: 'red',
  },
  // Action links share the last line, left-aligned so their labels stay flush
  // with the text above them however many of them a report happens to show.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

const patientShape = PropTypes.shape({
  id: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  birthDate: PropTypes.string,
  practiceMrn: PropTypes.string,
})

const deviceShape = PropTypes.shape({
  icmCapable: PropTypes.bool,
  icmActive: PropTypes.bool,
  deviceModel: PropTypes.shape({
    deviceType: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
})

// Always-visible summary: who the report is for, when it is from, and the
// signing/file state a reviewer scans before opening it.
export function ReportMobileCard({
  report,
  showSignAction = false,
  ordersRequired = false,
}) {
  const classes = useStyles()
  const {
    critical,
    device,
    hasBiobridgeReport,
    hasDeviceReport,
    hasOrder,
    patient,
    reportDate,
    signedAt,
  } = report

  // dayjs(undefined) / formatDOB(undefined) render today's date, so only format
  // values that are actually present.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const detailLine = [dob, device?.deviceModel?.deviceType?.name]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {critical && (
          <Tooltip title="Requires Attention">
            <ExclamationCircleOutlined className={classes.criticalIcon} />
          </Tooltip>
        )}
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {detailLine && <div className={classes.line}>{detailLine}</div>}
      <div className={classes.line}>
        <CalendarOutlined className={classes.lineIcon} />
        {reportDate ? dayjs(reportDate).format('ll') : '—'}
      </div>
      <div className={classes.line}>
        Signed <CircleCheckIcon checked={!!signedAt} /> · PDF{' '}
        <CircleCheckIcon checked={!!hasBiobridgeReport} /> · Full{' '}
        <CircleCheckIcon checked={!!hasDeviceReport} />
        {ordersRequired && (
          <>
            {' '}
            · Order <CircleCheckIcon checked={!!hasOrder} />
          </>
        )}
      </div>
      <div className={classes.actionsLine}>
        {!hasDeviceReport && (
          <Link to={reportEditPath(report)}>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ paddingLeft: 0 }}
            >
              Add device report
            </Button>
          </Link>
        )}
        {showSignAction && (
          <Link to={reportShowPath(report)}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              style={{ paddingLeft: 0 }}
            >
              Sign
            </Button>
          </Link>
        )}
        {hasDeviceReport && (
          <>
            <Link to={reportShowPath(report)}>
              <Button
                type="link"
                size="small"
                icon={<FileSearchOutlined />}
                style={{ paddingLeft: 0 }}
              >
                View
              </Button>
            </Link>
            <Link to={reportEditPath(report)}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                style={{ paddingLeft: 0 }}
              >
                Edit
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

ReportMobileCard.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.string.isRequired,
    critical: PropTypes.bool,
    hasBiobridgeReport: PropTypes.bool,
    hasDeviceReport: PropTypes.bool,
    hasOrder: PropTypes.bool,
    reportDate: PropTypes.string,
    signedAt: PropTypes.string,
    patient: patientShape,
    device: deviceShape,
  }).isRequired,
  showSignAction: PropTypes.bool,
  ordersRequired: PropTypes.bool,
}

// The remaining desktop columns, behind the per-row expand toggle.
export function ReportMobileDetails({ report, isAdmin = false, indent = 0 }) {
  const classes = useStyles()
  const { cptCode, device, icd10Code, patient, pdfExtraction } = report

  let icmActive = '—'
  if (device?.icmCapable) {
    icmActive = <CircleCheckIcon checked={!!device.icmActive} />
  }

  return (
    <div className={classes.detailsGrid} style={{ paddingLeft: indent }}>
      <Field label="MRN">{patient?.practiceMrn || '—'}</Field>
      <Field label="ICM active">{icmActive}</Field>
      <Field label="CPT code">{cptCode || '—'}</Field>
      <Field label="ICD-10 code">{icd10Code || '—'}</Field>
      {isAdmin && (
        <Field label="Parsing" span>
          {pdfExtraction ? (
            <DataExtractionIcon extractionStatus={pdfExtraction.status} />
          ) : (
            '—'
          )}
        </Field>
      )}
      <Field label="Stroke prevention" span>
        {patient ? <DiseasesTags patientSummary={patient} /> : '—'}
      </Field>
    </div>
  )
}

ReportMobileDetails.propTypes = {
  report: PropTypes.shape({
    cptCode: PropTypes.string,
    icd10Code: PropTypes.string,
    patient: patientShape,
    device: deviceShape,
    pdfExtraction: PropTypes.shape({
      status: PropTypes.number,
    }),
  }).isRequired,
  isAdmin: PropTypes.bool,
  indent: PropTypes.number,
}
